'use client';

import { token } from '@terra/ui-styled-system';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

const DEFAULT_BOX_SHADOW = `0 -4px 8px -2px ${token('colors.neutral.a3')}`;

type UseFooterScrollShadowResult = {
  scrollableRef: RefObject<HTMLDivElement | null>;
  scrollContentRef: RefObject<HTMLDivElement | null>;
  boxShadow: string | undefined;
  updateScrollState: () => void;
};

type UseFooterScrollShadowProps = {
  boxShadow?: string;
};

export const useFooterScrollShadow = ({
  boxShadow = DEFAULT_BOX_SHADOW,
}: UseFooterScrollShadowProps = {}): UseFooterScrollShadowResult => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [showFooterShadow, setShowFooterShadow] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!scrollableRef.current) {
      return;
    }

    const scrollableElement = scrollableRef.current;
    const hasScroll =
      scrollableElement.scrollHeight > scrollableElement.clientHeight;
    const isAtBottom =
      Math.ceil(scrollableElement.scrollTop + scrollableElement.clientHeight) >=
      scrollableElement.scrollHeight;

    setShowFooterShadow(hasScroll && !isAtBottom);
  }, []);

  useEffect(() => {
    if (!scrollableRef.current) {
      setShowFooterShadow(false);
      return;
    }

    updateScrollState();
    const scrollableElement = scrollableRef.current;
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(scrollableElement);

    const scrollContentElement = scrollContentRef.current;
    if (scrollContentElement) {
      resizeObserver.observe(scrollContentElement);
    }

    return () => resizeObserver.disconnect();
  }, [updateScrollState]);

  return {
    scrollableRef,
    scrollContentRef,
    boxShadow: showFooterShadow ? boxShadow : undefined,
    updateScrollState,
  };
};
